import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { ArrowLeft, Sparkles, Plus, Upload, Users as UsersIcon, Calendar, Settings, LogOut, RefreshCw, Link2, CheckCircle2, AlertCircle, Wand2, HelpCircle, CreditCard, Trash2 } from "lucide-react";
import { API_BASE } from "@/lib/api";
import OnboardingTour from "@/components/OnboardingTour";
import { TrialBanner, TrialPaywallModal, useWorkspaceStatus } from "@/components/TrialGuard";

const TOKEN_KEY = "nv_admin_jwt";

export default function Studio() {
  const nav = useNavigate();
  const [token, setToken] = useState(localStorage.getItem(TOKEN_KEY) || "");
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [customers, setCustomers] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [settings, setSettings] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [activeTab, setActiveTab] = useState("customers");
  const [showTour, setShowTour] = useState(false);
  const { status: wsStatus, paywallOpen, setPaywallOpen, refresh: refreshWsStatus, handle402 } = useWorkspaceStatus();

  const authed = (path, opts = {}) =>
    fetch(`${API_BASE}${path}`, { ...opts, headers: { ...(opts.headers || {}), Authorization: `Bearer ${token}` } })
      .then((resp) => {
        // Any 402 = trial expired → open paywall
        handle402(resp);
        return resp;
      });

  const loadAll = async (t = token) => {
    try {
      const [c, a, s, tpl] = await Promise.all([
        fetch(`${API_BASE}/product/customers`, { headers: { Authorization: `Bearer ${t}` } }),
        fetch(`${API_BASE}/product/appointments`, { headers: { Authorization: `Bearer ${t}` } }),
        fetch(`${API_BASE}/product/settings`, { headers: { Authorization: `Bearer ${t}` } }),
        fetch(`${API_BASE}/product/templates`, { headers: { Authorization: `Bearer ${t}` } }),
      ]);
      if (!c.ok || !a.ok || !s.ok || !tpl.ok) throw new Error("Unauthorized");
      setCustomers(await c.json());
      setAppointments(await a.json());
      setSettings(await s.json());
      setTemplates(await tpl.json());
    } catch {
      toast.error("Session expired");
      signOut();
    }
  };

  const checkTour = async (t = token) => {
    try {
      const r = await fetch(`${API_BASE}/product/tour-status`, { headers: { Authorization: `Bearer ${t}` } });
      if (!r.ok) return;
      const data = await r.json();
      if (!data.completed) {
        setShowTour(true);
      }
    } catch { /* silent */ }
  };

  const restartTour = async () => {
    try {
      await authed("/product/tour-reset", { method: "POST" });
      setActiveTab("customers");
      setShowTour(true);
    } catch { toast.error("Couldn't restart the tour."); }
  };

  const completeTour = async () => {
    setShowTour(false);
    try {
      await authed("/product/tour-complete", { method: "POST" });
    } catch { /* best effort */ }
  };

  useEffect(() => {
    const saved = localStorage.getItem(TOKEN_KEY);
    if (!saved) { nav("/admin"); return; }
    (async () => {
      try {
        const r = await fetch(`${API_BASE}/auth/me`, { headers: { Authorization: `Bearer ${saved}` } });
        if (!r.ok) throw new Error();
        setUser(await r.json());
        setToken(saved);
        await loadAll(saved);
        await checkTour(saved);
        await refreshWsStatus();
      } catch {
        localStorage.removeItem(TOKEN_KEY);
        nav("/admin");
      } finally {
        setChecking(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const signOut = () => {
    localStorage.removeItem(TOKEN_KEY);
    nav("/admin");
  };

  if (checking) {
    return <div className="min-h-screen bg-[#FAF7F0] flex items-center justify-center"><div className="text-[#4A4A4A]">Loading…</div></div>;
  }

  return (
    <div className="min-h-screen bg-[#FAF7F0]">
      <TrialBanner status={wsStatus} onUpgrade={() => setPaywallOpen(true)} />
      <TrialPaywallModal open={paywallOpen} onOpenChange={setPaywallOpen} status={wsStatus} />
      <div className="border-b border-[#E5E0D8] bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <div className="flex items-center gap-6">
            <Link to="/admin" className="text-sm text-[#4A4A4A] hover:text-[#183D36] flex items-center gap-2">
              <ArrowLeft size={14} /> Admin
            </Link>
            <div className="flex items-center gap-2">
              <span className="w-8 h-8 rounded-2xl bg-[#183D36] text-[#F4B183] flex items-center justify-center">
                <Sparkles size={14} />
              </span>
              <h1 className="font-heading text-lg text-[#183D36]">Studio</h1>
              <span className="text-xs font-accent uppercase tracking-widest text-[#4A4A4A] ml-1">— customer messaging</span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="hidden sm:inline text-sm text-[#4A4A4A]">{user?.email}</span>
            <Button variant="ghost" size="sm" onClick={restartTour} className="rounded-full hidden md:inline-flex" data-testid="studio-tour-restart">
              <HelpCircle size={14} className="mr-2" /> Tour
            </Button>
            {wsStatus?.has_stripe_customer && (
              <Button
                variant="ghost"
                size="sm"
                onClick={async () => {
                  try {
                    const r = await fetch(`${API_BASE}/billing/portal`, {
                      method: "POST",
                      headers: { Authorization: `Bearer ${token}` },
                    });
                    if (!r.ok) throw new Error("portal failed");
                    const data = await r.json();
                    if (data.url) window.location.href = data.url;
                  } catch {
                    toast.error("Couldn't open billing portal.");
                  }
                }}
                className="rounded-full hidden md:inline-flex"
                data-testid="studio-manage-billing"
              >
                <CreditCard size={14} className="mr-2" /> Billing
              </Button>
            )}
            <Button variant="ghost" size="sm" onClick={() => loadAll()} className="rounded-full" data-testid="studio-refresh">
              <RefreshCw size={14} className="mr-2" /> Refresh
            </Button>
            <Button variant="ghost" size="sm" onClick={signOut} className="rounded-full" data-testid="studio-signout">
              <LogOut size={14} className="mr-2" /> Sign out
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-8">
          <StatCard icon={UsersIcon} label="Customers" value={customers.length} testid="studio-stat-customers" />
          <StatCard icon={Calendar} label="Appointments" value={appointments.length} testid="studio-stat-appointments" />
          <StatCard icon={Settings} label="Active templates" value={templates.filter(t => t.enabled).length} testid="studio-stat-templates" />
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="mb-6 rounded-full bg-white border border-[#E5E0D8] p-1">
            <TabsTrigger value="customers" data-testid="studio-tab-customers" className="rounded-full data-[state=active]:bg-[#183D36] data-[state=active]:text-[#FAF7F0]">Customers</TabsTrigger>
            <TabsTrigger value="appointments" data-testid="studio-tab-appointments" className="rounded-full data-[state=active]:bg-[#183D36] data-[state=active]:text-[#FAF7F0]">Appointments</TabsTrigger>
            <TabsTrigger value="integrations" data-testid="studio-tab-integrations" className="rounded-full data-[state=active]:bg-[#183D36] data-[state=active]:text-[#FAF7F0]">Integrations</TabsTrigger>
            <TabsTrigger value="team" data-testid="studio-tab-team" className="rounded-full data-[state=active]:bg-[#183D36] data-[state=active]:text-[#FAF7F0]">Team</TabsTrigger>
            <TabsTrigger value="settings" data-testid="studio-tab-settings" className="rounded-full data-[state=active]:bg-[#183D36] data-[state=active]:text-[#FAF7F0]">Voice & Templates</TabsTrigger>
          </TabsList>

          <TabsContent value="customers">
            <CustomersTab customers={customers} authed={authed} onRefresh={() => loadAll()} />
          </TabsContent>
          <TabsContent value="appointments">
            <AppointmentsTab appointments={appointments} customers={customers} authed={authed} onRefresh={() => loadAll()} />
          </TabsContent>
          <TabsContent value="integrations">
            <IntegrationsTab authed={authed} onRefresh={() => loadAll()} onGoToAppointments={() => setActiveTab("appointments")} />
          </TabsContent>
          <TabsContent value="team">
            <TeamTab authed={authed} currentUserId={user?.id} currentUserRole={user?.role} />
          </TabsContent>
          <TabsContent value="settings">
            <SettingsTab settings={settings} templates={templates} authed={authed} onRefresh={() => loadAll()} />
          </TabsContent>
        </Tabs>
      </div>

      {showTour && (
        <OnboardingTour
          onDone={completeTour}
          steps={[
            {
              title: `Welcome${user?.name ? `, ${user.name.split(" ")[0]}` : ""}! Your Studio is live.`,
              body: (
                <>
                  Every follow-up NextVisit sends — thank-yous, review requests, rebook nudges, win-backs — runs from this dashboard. Let&apos;s take 60 seconds to make sure it&apos;s dialed in for <b>{settings?.business_name || "your business"}</b>.
                </>
              ),
            },
            {
              targetTestId: "studio-tab-settings",
              placement: "bottom",
              title: "Your 4 message templates",
              body: (
                <>
                  We&apos;ve pre-loaded four templates tuned for your business type. Edit any of them, or use <b>Draft with AI</b> to have Claude rewrite them in your voice.
                </>
              ),
              beforeShow: () => setActiveTab("settings"),
            },
            {
              targetTestId: "studio-settings-review",
              placement: "top",
              title: "Add your review link",
              body: (
                <>
                  Paste your Google or Yelp review URL here. If this is blank, we skip the review email automatically — no awkward broken links.
                </>
              ),
              beforeShow: () => setActiveTab("settings"),
            },
            {
              targetTestId: "studio-tab-integrations",
              placement: "bottom",
              title: "Connect Square (optional)",
              body: (
                <>
                  Sync bookings from Square Appointments so every new client gets the full sequence automatically. Or skip this and log appointments manually.
                </>
              ),
              beforeShow: () => setActiveTab("integrations"),
            },
            {
              targetTestId: "studio-tab-appointments",
              placement: "bottom",
              title: "Log a test appointment",
              body: (
                <>
                  Use your own email + a time a few minutes from now. You&apos;ll receive the actual thank-you your customers get — the fastest way to see the magic land.
                </>
              ),
              beforeShow: () => setActiveTab("appointments"),
            },
            {
              title: "You're all set.",
              body: (
                <>
                  As appointments come in — through Square or logged manually — NextVisit handles the rest. If you need to see this tour again, hit the <b>Tour</b> button in the top-right.
                </>
              ),
              beforeShow: () => setActiveTab("customers"),
            },
          ]}
        />
      )}
    </div>
  );
}

function StatCard({ icon: Icon, label, value, testid }) {
  return (
    <div className="rounded-2xl bg-white border border-[#E5E0D8] p-6" data-testid={testid}>
      <div className="flex items-center gap-3">
        <span className="w-10 h-10 rounded-2xl bg-[#A8BFA3]/40 flex items-center justify-center">
          <Icon size={16} className="text-[#183D36]" />
        </span>
        <span className="text-sm text-[#4A4A4A]">{label}</span>
      </div>
      <div className="font-heading text-3xl text-[#183D36] mt-3">{value}</div>
    </div>
  );
}

/* -------------------- Customers -------------------- */
function CustomersTab({ customers, authed, onRefresh }) {
  const [open, setOpen] = useState(false);
  const [csvOpen, setCsvOpen] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const removeCustomer = async (c) => {
    if (!window.confirm(`Delete ${c.name}? This also removes their appointments and cancels pending follow-ups.`)) return;
    setDeletingId(c.id);
    try {
      const r = await authed(`/product/customers/${c.id}`, { method: "DELETE" });
      if (!r.ok) throw new Error();
      toast.success("Customer deleted");
      onRefresh();
    } catch {
      toast.error("Failed to delete customer");
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="rounded-3xl bg-white border border-[#E5E0D8] overflow-hidden">
      <div className="p-6 border-b border-[#E5E0D8] flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div>
          <h2 className="font-heading text-xl text-[#183D36]">Customers</h2>
          <p className="text-sm text-[#4A4A4A] mt-1">Everyone who&apos;s had an appointment with you.</p>
        </div>
        <div className="flex gap-2">
          <Button data-testid="studio-import-csv-btn" variant="ghost" onClick={() => setCsvOpen(true)} className="rounded-full border border-[#183D36]/20">
            <Upload size={14} className="mr-2" /> Import CSV
          </Button>
          <Button data-testid="studio-add-customer-btn" onClick={() => setOpen(true)} className="rounded-full bg-[#183D36] hover:bg-[#122e29] text-[#FAF7F0]">
            <Plus size={14} className="mr-2" /> Add customer
          </Button>
        </div>
      </div>
      <div className="overflow-x-auto">
        <Table data-testid="studio-customers-table">
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Phone</TableHead>
              <TableHead>SMS</TableHead>
              <TableHead>Notes</TableHead>
              <TableHead>Added</TableHead>
              <TableHead className="w-[72px]" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {customers.length === 0 ? (
              <TableRow><TableCell colSpan={6} className="text-center text-[#4A4A4A] py-10">No customers yet. Add one to get started.</TableCell></TableRow>
            ) : customers.map((c) => (
              <TableRow key={c.id} data-testid={`studio-customer-row-${c.id}`}>
                <TableCell className="font-medium text-[#183D36]">{c.name}</TableCell>
                <TableCell>{c.email}{c.unsubscribed && <span className="ml-2 text-xs text-[#F4B183]">unsub</span>}</TableCell>
                <TableCell>{c.phone || "—"}</TableCell>
                <TableCell className="text-xs text-[#4A4A4A]">
                  {c.sms_opt_in ? <span className="text-[#183D36]">Opted in</span> : "—"}
                </TableCell>
                <TableCell className="text-sm text-[#4A4A4A] max-w-xs truncate">{c.notes || "—"}</TableCell>
                <TableCell className="text-sm text-[#4A4A4A]">{new Date(c.created_at).toLocaleDateString()}</TableCell>
                <TableCell>
                  <Button
                    data-testid={`studio-customer-delete-${c.id}`}
                    variant="ghost"
                    size="sm"
                    disabled={deletingId === c.id}
                    onClick={() => removeCustomer(c)}
                    className="h-8 w-8 p-0 text-red-700 hover:bg-red-50 hover:text-red-800"
                    aria-label={`Delete ${c.name}`}
                  >
                    <Trash2 size={14} />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <CustomerDialog open={open} onOpenChange={setOpen} authed={authed} onSaved={() => { onRefresh(); setOpen(false); }} />
      <CsvImportDialog open={csvOpen} onOpenChange={setCsvOpen} authed={authed} onSaved={() => { onRefresh(); setCsvOpen(false); }} />
    </div>
  );
}

const SMS_OPT_IN_COPY =
  "I confirm this customer agreed to receive SMS appointment follow-ups (thank-you, review request, and rebook reminders) from my business via NextVisit at the number provided. Msg & data rates may apply. Reply STOP to opt out, HELP for help. Consent is not a condition of purchase.";

function CustomerDialog({ open, onOpenChange, authed, onSaved }) {
  const [f, setF] = useState({ name: "", email: "", phone: "", notes: "", sms_opt_in: false });
  const [busy, setBusy] = useState(false);
  const phoneEntered = Boolean((f.phone || "").trim());

  const submit = async () => {
    if (!f.name || !f.email) { toast.error("Name and email required"); return; }
    if (f.sms_opt_in && !phoneEntered) {
      toast.error("Add a phone number before enabling SMS opt-in");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        name: f.name,
        email: f.email,
        phone: (f.phone || "").trim() || null,
        notes: f.notes || null,
        // Phone may be stored for records; SMS sends require this explicit opt-in.
        sms_opt_in: phoneEntered ? Boolean(f.sms_opt_in) : false,
      };
      const r = await authed("/product/customers", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || "Failed to save");
      }
      toast.success("Customer added");
      setF({ name: "", email: "", phone: "", notes: "", sms_opt_in: false });
      onSaved();
    } catch (e) {
      toast.error(String(e.message || e) || "Failed to save");
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-3xl bg-[#FAF7F0] max-w-lg" data-testid="studio-customer-dialog">
        <DialogHeader><DialogTitle className="font-heading text-2xl text-[#183D36]">New customer</DialogTitle></DialogHeader>
        <div className="grid gap-4 py-2">
          <div className="grid gap-2"><Label>Name</Label><Input data-testid="studio-cust-name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Maya Park" /></div>
          <div className="grid gap-2"><Label>Email</Label><Input data-testid="studio-cust-email" type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} placeholder="maya@example.com" /></div>
          <div className="grid gap-2">
            <Label>Phone (optional — required for SMS)</Label>
            <Input
              data-testid="studio-cust-phone"
              value={f.phone}
              onChange={(e) => {
                const phone = e.target.value;
                setF({ ...f, phone, sms_opt_in: phone.trim() ? f.sms_opt_in : false });
              }}
              placeholder="(555) 123-4567"
            />
          </div>
          {phoneEntered && (
            <div className="rounded-2xl border border-[#183D36]/15 bg-white p-4" data-testid="studio-cust-sms-opt-in-block">
              <div className="flex items-start gap-3">
                <Checkbox
                  id="studio-cust-sms-opt-in"
                  data-testid="studio-cust-sms-opt-in"
                  checked={f.sms_opt_in}
                  onCheckedChange={(checked) => setF({ ...f, sms_opt_in: checked === true })}
                  className="mt-0.5 border-[#183D36] data-[state=checked]:bg-[#183D36]"
                />
                <label htmlFor="studio-cust-sms-opt-in" className="text-sm text-[#252525] leading-relaxed cursor-pointer">
                  {SMS_OPT_IN_COPY}{" "}
                  <a href="/messaging-consent" target="_blank" rel="noreferrer" className="text-[#183D36] underline">Messaging consent</a>
                  {" · "}
                  <a href="/privacy" target="_blank" rel="noreferrer" className="text-[#183D36] underline">Privacy</a>
                  {" · "}
                  <a href="/terms" target="_blank" rel="noreferrer" className="text-[#183D36] underline">Terms</a>
                </label>
              </div>
            </div>
          )}
          <div className="grid gap-2"><Label>Notes (optional)</Label><Textarea data-testid="studio-cust-notes" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} placeholder="Prefers balayage, comes every 6 weeks" /></div>
        </div>
        <DialogFooter>
          <Button data-testid="studio-cust-submit" disabled={busy} onClick={submit} className="rounded-full bg-[#183D36] hover:bg-[#122e29] text-[#FAF7F0] px-6">
            {busy ? "Saving…" : "Save customer"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CsvImportDialog({ open, onOpenChange, authed, onSaved }) {
  // Steps: 1=upload, 2=map columns + preview, 3=result
  const [step, setStep] = useState(1);
  const [csv, setCsv] = useState("");
  const [fileName, setFileName] = useState("");
  const [preview, setPreview] = useState(null); // {columns, sample_rows, suggested_map, total_rows}
  const [map, setMap] = useState({ email: "", name: "", phone: "", notes: "" });
  const [smsConsentAttested, setSmsConsentAttested] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null); // {created, skipped_duplicates, skipped_invalid, errors, total_rows}

  const reset = () => {
    setStep(1);
    setCsv("");
    setFileName("");
    setPreview(null);
    setMap({ email: "", name: "", phone: "", notes: "" });
    setSmsConsentAttested(false);
    setResult(null);
    setBusy(false);
  };
  const close = (v) => { if (!v) reset(); onOpenChange(v); };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("CSV too large (max 5MB)"); return; }
    const text = await file.text();
    setCsv(text);
    setFileName(file.name);
  };

  const runPreview = async () => {
    if (!csv.trim()) { toast.error("Paste CSV or choose a file first"); return; }
    setBusy(true);
    try {
      const r = await authed("/product/customers/import/preview", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ csv }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || "Preview failed");
      setPreview(data);
      setMap({
        email: data.suggested_map?.email || "",
        name: data.suggested_map?.name || "",
        phone: data.suggested_map?.phone || "",
        notes: data.suggested_map?.notes || "",
      });
      setStep(2);
    } catch (e) {
      toast.error(String(e.message || e));
    } finally {
      setBusy(false);
    }
  };

  const runCommit = async () => {
    if (!map.email) { toast.error("Map an 'email' column before importing"); return; }
    if (map.phone && !smsConsentAttested) {
      toast.error("Confirm SMS consent before importing phone numbers");
      return;
    }
    setBusy(true);
    try {
      const r = await authed("/product/customers/import/commit", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          csv,
          column_map: map,
          sms_consent_attested: map.phone ? smsConsentAttested : false,
        }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || "Import failed");
      setResult(data);
      setStep(3);
      if (data.created > 0) toast.success(`Imported ${data.created} customer(s)`);
    } catch (e) {
      toast.error(String(e.message || e));
    } finally {
      setBusy(false);
    }
  };

  const finish = () => { onSaved(); reset(); };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="rounded-3xl bg-[#FAF7F0] max-w-2xl" data-testid="studio-csv-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl text-[#183D36]">
            {step === 1 && "Import customers from CSV"}
            {step === 2 && "Map your columns"}
            {step === 3 && "Import complete"}
          </DialogTitle>
        </DialogHeader>

        {step === 1 && (
          <div className="space-y-4">
            <p className="text-sm text-[#4A4A4A]">
              Upload a CSV file exported from Vagaro, Mindbody, Google Sheets, or anywhere else. We&apos;ll auto-detect your columns on the next step. Max 5,000 rows.
            </p>
            <div className="rounded-2xl border-2 border-dashed border-[#183D36]/20 p-6 text-center bg-white">
              <Upload size={22} className="mx-auto text-[#183D36] mb-2" />
              <input
                type="file"
                accept=".csv,text/csv"
                onChange={onFile}
                className="text-sm w-full"
                data-testid="studio-csv-file-input"
              />
              {fileName && <p className="text-xs text-[#4A4A4A] mt-2">Selected: <b>{fileName}</b></p>}
            </div>
            <div className="text-xs text-[#4A4A4A]">— or paste CSV text —</div>
            <Textarea
              data-testid="studio-csv-textarea"
              value={csv}
              onChange={(e) => { setCsv(e.target.value); setFileName(""); }}
              className="font-mono text-xs min-h-[140px]"
              placeholder="Full Name,Email Address,Cell,Notes&#10;Maya Park,maya@example.com,555-1234,Every 6 weeks"
            />
            <DialogFooter>
              <Button data-testid="studio-csv-preview-btn" disabled={busy || !csv.trim()} onClick={runPreview} className="rounded-full bg-[#183D36] hover:bg-[#122e29] text-[#FAF7F0] px-6">
                {busy ? "Reading…" : "Continue →"}
              </Button>
            </DialogFooter>
          </div>
        )}

        {step === 2 && preview && (
          <div className="space-y-4">
            <p className="text-sm text-[#4A4A4A]">
              Detected <b>{preview.total_rows}</b> rows and <b>{preview.columns.length}</b> columns. Confirm which column maps to what — we&apos;ve guessed based on your headers.
            </p>
            <div className="grid grid-cols-2 gap-3">
              <MapField label="Email" required value={map.email} columns={preview.columns} onChange={(v) => setMap({ ...map, email: v })} testid="studio-map-email" />
              <MapField label="Name" value={map.name} columns={preview.columns} onChange={(v) => setMap({ ...map, name: v })} testid="studio-map-name" />
              <MapField label="Phone" value={map.phone} columns={preview.columns} onChange={(v) => setMap({ ...map, phone: v })} testid="studio-map-phone" />
              <MapField label="Notes" value={map.notes} columns={preview.columns} onChange={(v) => setMap({ ...map, notes: v })} testid="studio-map-notes" />
            </div>
            <div>
              <div className="text-xs text-[#4A4A4A] mb-1">Sample rows:</div>
              <div className="rounded-xl bg-white border border-[#E5E0D8] overflow-x-auto text-xs max-h-40">
                <table className="w-full">
                  <thead className="bg-[#EFECE5]">
                    <tr>{preview.columns.map((c) => <th key={c} className="px-2 py-1 text-left text-[#183D36] font-medium">{c}</th>)}</tr>
                  </thead>
                  <tbody>
                    {preview.sample_rows.map((row, i) => (
                      <tr key={i} className="border-t border-[#E5E0D8]">
                        {preview.columns.map((c) => <td key={c} className="px-2 py-1 truncate max-w-[180px]">{row[c]}</td>)}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            {map.phone && (
              <div className="rounded-2xl border border-[#183D36]/15 bg-white p-4" data-testid="studio-csv-sms-consent-block">
                <div className="flex items-start gap-3">
                  <Checkbox
                    id="studio-csv-sms-consent"
                    data-testid="studio-csv-sms-consent"
                    checked={smsConsentAttested}
                    onCheckedChange={(checked) => setSmsConsentAttested(checked === true)}
                    className="mt-0.5 border-[#183D36] data-[state=checked]:bg-[#183D36]"
                  />
                  <label htmlFor="studio-csv-sms-consent" className="text-sm text-[#252525] leading-relaxed cursor-pointer">
                    I confirm every imported phone number belongs to a customer who already agreed to receive SMS appointment follow-ups from my business via NextVisit. Msg &amp; data rates may apply. Reply STOP to opt out, HELP for help. See our{" "}
                    <a href="/messaging-consent" target="_blank" rel="noreferrer" className="text-[#183D36] underline">messaging consent</a> page.
                  </label>
                </div>
              </div>
            )}
            <DialogFooter className="gap-2">
              <Button variant="ghost" onClick={() => setStep(1)} className="rounded-full">← Back</Button>
              <Button
                data-testid="studio-csv-commit-btn"
                disabled={busy || !map.email || (Boolean(map.phone) && !smsConsentAttested)}
                onClick={runCommit}
                className="rounded-full bg-[#183D36] hover:bg-[#122e29] text-[#FAF7F0] px-6"
              >
                {busy ? "Importing…" : `Import ${preview.total_rows} row${preview.total_rows === 1 ? "" : "s"}`}
              </Button>
            </DialogFooter>
          </div>
        )}

        {step === 3 && result && (
          <div className="space-y-4">
            <div className="rounded-2xl bg-white border border-[#E5E0D8] p-5 grid grid-cols-3 gap-4 text-center">
              <ResultStat label="Added" value={result.created} tone="good" />
              <ResultStat label="Duplicates skipped" value={result.skipped_duplicates} tone="neutral" />
              <ResultStat label="Invalid skipped" value={result.skipped_invalid} tone={result.skipped_invalid > 0 ? "warn" : "neutral"} />
            </div>
            {result.errors?.length > 0 && (
              <div className="rounded-xl bg-[#F4B183]/10 border border-[#F4B183]/40 p-3">
                <div className="text-sm font-medium text-[#183D36] mb-1">First {result.errors.length} error(s):</div>
                <ul className="text-xs text-[#4A4A4A] space-y-0.5">
                  {result.errors.map((e, i) => <li key={i}>{e}</li>)}
                </ul>
              </div>
            )}
            <DialogFooter>
              <Button data-testid="studio-csv-done-btn" onClick={finish} className="rounded-full bg-[#183D36] hover:bg-[#122e29] text-[#FAF7F0] px-6">Done</Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function MapField({ label, value, columns, onChange, required, testid }) {
  return (
    <div className="grid gap-1">
      <Label className="text-xs text-[#4A4A4A]">
        {label}{required && <span className="text-[#F4B183] ml-1">*</span>}
      </Label>
      <select
        data-testid={testid}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="h-9 rounded-full border border-[#E5E0D8] bg-white px-3 text-sm text-[#252525]"
      >
        <option value="">— skip —</option>
        {columns.map((c) => <option key={c} value={c}>{c}</option>)}
      </select>
    </div>
  );
}

function ResultStat({ label, value, tone }) {
  const color = tone === "good" ? "text-[#183D36]" : tone === "warn" ? "text-[#F4B183]" : "text-[#4A4A4A]";
  return (
    <div>
      <div className={`font-heading text-3xl ${color}`}>{value}</div>
      <div className="text-xs text-[#4A4A4A] mt-1">{label}</div>
    </div>
  );
}

/* -------------------- Appointments -------------------- */
function AppointmentsTab({ appointments, customers, authed, onRefresh }) {
  const [open, setOpen] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const removeAppointment = async (a) => {
    if (!window.confirm(`Delete this appointment for ${a.customer_name || "customer"}? Pending follow-ups from it will be cancelled.`)) return;
    setDeletingId(a.id);
    try {
      const r = await authed(`/product/appointments/${a.id}`, { method: "DELETE" });
      if (!r.ok) throw new Error();
      toast.success("Appointment deleted");
      onRefresh();
    } catch {
      toast.error("Failed to delete appointment");
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="rounded-3xl bg-white border border-[#E5E0D8] overflow-hidden">
      <div className="p-6 border-b border-[#E5E0D8] flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div>
          <h2 className="font-heading text-xl text-[#183D36]">Appointments</h2>
          <p className="text-sm text-[#4A4A4A] mt-1">Every appointment auto-schedules a thank-you, a review request, and a rebook nudge. Stale customers get a win-back automatically.</p>
        </div>
        <Button data-testid="studio-add-appt-btn" onClick={() => setOpen(true)} disabled={customers.length === 0} className="rounded-full bg-[#183D36] hover:bg-[#122e29] text-[#FAF7F0]">
          <Plus size={14} className="mr-2" /> Log appointment
        </Button>
      </div>
      <div className="overflow-x-auto">
        <Table data-testid="studio-appts-table">
          <TableHeader>
            <TableRow>
              <TableHead>Customer</TableHead>
              <TableHead>Service</TableHead>
              <TableHead>When</TableHead>
              <TableHead>Price</TableHead>
              <TableHead>Notes</TableHead>
              <TableHead className="w-[72px]" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {appointments.length === 0 ? (
              <TableRow><TableCell colSpan={6} className="text-center text-[#4A4A4A] py-10">No appointments logged yet.</TableCell></TableRow>
            ) : appointments.map((a) => (
              <TableRow key={a.id} data-testid={`studio-appt-row-${a.id}`}>
                <TableCell className="font-medium text-[#183D36]">{a.customer_name || "—"}<div className="text-xs text-[#4A4A4A]">{a.customer_email}</div></TableCell>
                <TableCell>{a.service}</TableCell>
                <TableCell className="text-sm">{new Date(a.appointment_at).toLocaleString()}</TableCell>
                <TableCell className="text-sm">{a.price != null ? `$${a.price.toFixed(2)}` : "—"}</TableCell>
                <TableCell className="text-sm text-[#4A4A4A] max-w-xs truncate">{a.notes || "—"}</TableCell>
                <TableCell>
                  <Button
                    data-testid={`studio-appt-delete-${a.id}`}
                    variant="ghost"
                    size="sm"
                    disabled={deletingId === a.id}
                    onClick={() => removeAppointment(a)}
                    className="h-8 w-8 p-0 text-red-700 hover:bg-red-50 hover:text-red-800"
                    aria-label="Delete appointment"
                  >
                    <Trash2 size={14} />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <AppointmentDialog open={open} onOpenChange={setOpen} customers={customers} authed={authed} onSaved={() => { onRefresh(); setOpen(false); }} />
    </div>
  );
}

function AppointmentDialog({ open, onOpenChange, customers, authed, onSaved }) {
  const [f, setF] = useState({ customer_id: "", service: "", appointment_at: "", price: "", notes: "" });
  const [busy, setBusy] = useState(false);
  const [previews, setPreviews] = useState([]);
  const [previewLoading, setPreviewLoading] = useState(false);

  // Debounced live preview
  useEffect(() => {
    if (!open) return;
    if (!f.customer_id || !f.service || !f.appointment_at) {
      setPreviews([]);
      return;
    }
    setPreviewLoading(true);
    const handle = setTimeout(async () => {
      try {
        const r = await authed("/product/preview-messages", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            customer_id: f.customer_id,
            service: f.service,
            appointment_at: new Date(f.appointment_at).toISOString(),
          }),
        });
        if (r.ok) {
          const data = await r.json();
          setPreviews(data.previews || []);
        }
      } catch { /* ignore preview errors */ }
      finally { setPreviewLoading(false); }
    }, 400);
    return () => clearTimeout(handle);
  }, [f.customer_id, f.service, f.appointment_at, open, authed]);

  const submit = async () => {
    if (!f.customer_id || !f.service || !f.appointment_at) { toast.error("Customer, service, and date/time required"); return; }
    setBusy(true);
    try {
      const payload = {
        customer_id: f.customer_id,
        service: f.service,
        appointment_at: new Date(f.appointment_at).toISOString(),
        price: f.price ? parseFloat(f.price) : null,
        notes: f.notes || null,
      };
      const r = await authed("/product/appointments", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload) });
      if (!r.ok) throw new Error();
      toast.success("Appointment logged — messages scheduled");
      setF({ customer_id: "", service: "", appointment_at: "", price: "", notes: "" });
      setPreviews([]);
      onSaved();
    } catch {
      toast.error("Failed to save");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-3xl bg-[#FAF7F0] max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="studio-appt-dialog">
        <DialogHeader><DialogTitle className="font-heading text-2xl text-[#183D36]">Log appointment</DialogTitle></DialogHeader>
        <div className="grid gap-6 md:grid-cols-5 py-2">
          {/* Form column */}
          <div className="md:col-span-2 grid gap-4">
            <div className="grid gap-2">
              <Label>Customer</Label>
              <Select value={f.customer_id} onValueChange={(v) => setF({ ...f, customer_id: v })}>
                <SelectTrigger data-testid="studio-appt-customer" className="rounded-full"><SelectValue placeholder="Pick a customer" /></SelectTrigger>
                <SelectContent>{customers.map(c => <SelectItem key={c.id} value={c.id}>{c.name} — {c.email}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid gap-2"><Label>Service</Label><Input data-testid="studio-appt-service" value={f.service} onChange={(e) => setF({ ...f, service: e.target.value })} placeholder="Balayage" /></div>
            <div className="grid gap-2"><Label>Date & time</Label><Input data-testid="studio-appt-when" type="datetime-local" value={f.appointment_at} onChange={(e) => setF({ ...f, appointment_at: e.target.value })} /></div>
            <div className="grid gap-2"><Label>Price (optional)</Label><Input data-testid="studio-appt-price" type="number" step="0.01" value={f.price} onChange={(e) => setF({ ...f, price: e.target.value })} placeholder="180.00" /></div>
            <div className="grid gap-2"><Label>Notes (optional)</Label><Textarea data-testid="studio-appt-notes" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} placeholder="Toned to warm honey" /></div>
          </div>

          {/* Preview column */}
          <div className="md:col-span-3 rounded-2xl bg-[#EFECE5] border border-[#E5E0D8] p-5" data-testid="studio-appt-preview-panel">
            <div className="overline mb-3">Live preview — what will be sent</div>
            {(!f.customer_id || !f.service || !f.appointment_at) ? (
              <p className="text-sm text-[#4A4A4A]">Fill in a customer, service, and date/time to see the two messages that will auto-schedule.</p>
            ) : previewLoading && previews.length === 0 ? (
              <p className="text-sm text-[#4A4A4A]">Rendering preview…</p>
            ) : previews.length === 0 ? (
              <p className="text-sm text-[#4A4A4A]">No templates enabled. Enable them in Voice & Templates.</p>
            ) : (
              <div className="space-y-4">
                {previews.map((p) => (
                  <div key={p.kind} className={`rounded-2xl bg-white border border-[#E5E0D8] p-4 ${!p.enabled ? "opacity-50" : ""}`} data-testid={`studio-preview-${p.kind}`}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-accent uppercase tracking-widest text-[#F4B183] font-semibold">
                        {labelForKind(p.kind)}{!p.enabled && " · disabled"}
                      </span>
                      <span className="text-xs text-[#4A4A4A]">
                        {new Date(p.send_at).toLocaleString()}
                      </span>
                    </div>
                    <div className="font-heading text-sm text-[#183D36] mb-2">{p.subject}</div>
                    <pre className="text-xs text-[#252525] whitespace-pre-wrap font-sans leading-relaxed" data-testid={`studio-preview-body-${p.kind}`}>{p.body}</pre>
                    <div className="text-[10px] text-[#4A4A4A] mt-3 font-accent uppercase tracking-widest">
                      {p.kind === "winback"
                        ? <>Sends when the customer&apos;s last visit was {p.delay_value}+ {p.delay_unit} ago</>
                        : <>Sends {p.delay_value} {p.delay_unit} after appointment</>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button data-testid="studio-appt-submit" disabled={busy} onClick={submit} className="rounded-full bg-[#183D36] hover:bg-[#122e29] text-[#FAF7F0] px-6">
            {busy ? "Saving…" : "Log & schedule messages"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* -------------------- Settings + Templates -------------------- */

/* -------------------- Team members -------------------- */
function TeamTab({ authed, currentUserId, currentUserRole }) {
  const [data, setData] = useState({ members: [], pending_invites: [] });
  const [loading, setLoading] = useState(true);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("member");
  const [inviting, setInviting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await authed("/workspaces/members");
      if (r.ok) setData(await r.json());
    } catch { /* ignore */ }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const invite = async () => {
    if (!inviteEmail.trim()) { toast.error("Enter an email"); return; }
    setInviting(true);
    try {
      const r = await authed("/workspaces/invite", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: inviteEmail.trim().toLowerCase(), role: inviteRole }),
      });
      const d = await r.json();
      if (!r.ok) { toast.error(typeof d.detail === "string" ? d.detail : "Invite failed"); return; }
      toast.success(`Invite sent to ${inviteEmail}`);
      setInviteEmail("");
      load();
    } catch (e) { toast.error(String(e.message || e)); }
    finally { setInviting(false); }
  };

  const remove = async (id, email) => {
    if (!window.confirm(`Remove ${email} from your workspace?`)) return;
    try {
      const r = await authed(`/workspaces/members/${id}`, { method: "DELETE" });
      if (!r.ok) { toast.error("Couldn't remove that user"); return; }
      toast.success(`${email} removed`);
      load();
    } catch { toast.error("Remove failed"); }
  };

  const revoke = async (id, email) => {
    if (!window.confirm(`Revoke the invite sent to ${email}?`)) return;
    try {
      const r = await authed(`/workspaces/invites/${id}`, { method: "DELETE" });
      if (!r.ok) { toast.error("Couldn't revoke"); return; }
      toast.success("Invite revoked");
      load();
    } catch { toast.error("Revoke failed"); }
  };

  const canManage = currentUserRole === "owner" || currentUserRole === "admin";

  return (
    <div className="grid gap-6" data-testid="team-tab">
      <div className="rounded-3xl bg-white border border-[#E5E0D8] p-8">
        <div className="flex items-start justify-between gap-6 flex-wrap mb-4">
          <div>
            <div className="overline mb-1">Workspace team</div>
            <h2 className="font-heading text-2xl text-[#183D36]">Team members</h2>
            <p className="text-sm text-[#4A4A4A] mt-1">People who can access this workspace&apos;s customers, appointments, and templates.</p>
          </div>
        </div>

        {canManage && (
          <div className="rounded-2xl bg-[#FAF7F0] border border-[#E5E0D8] p-5 mb-6" data-testid="team-invite-panel">
            <p className="text-sm font-medium text-[#183D36] mb-3">Invite a teammate</p>
            <div className="grid gap-3 md:grid-cols-[1fr,180px,auto] md:items-end">
              <div className="grid gap-1">
                <Label className="text-xs">Email</Label>
                <Input
                  data-testid="team-invite-email"
                  type="email"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  placeholder="teammate@business.com"
                />
              </div>
              <div className="grid gap-1">
                <Label className="text-xs">Role</Label>
                <Select value={inviteRole} onValueChange={setInviteRole}>
                  <SelectTrigger data-testid="team-invite-role" className="rounded-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="member">Member — full access</SelectItem>
                    <SelectItem value="owner">Owner — can invite/remove others</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button
                data-testid="team-invite-submit"
                onClick={invite}
                disabled={inviting}
                className="rounded-full bg-[#183D36] hover:bg-[#122e29] text-[#FAF7F0]"
              >
                {inviting ? "Sending…" : "Send invite"}
              </Button>
            </div>
            <p className="text-xs text-[#4A4A4A] mt-3">
              We&apos;ll send them an email with a link to set their password and join your workspace. Invites expire in 7 days.
            </p>
          </div>
        )}

        {loading ? (
          <p className="text-sm text-[#4A4A4A]">Loading team…</p>
        ) : (
          <div className="rounded-2xl border border-[#E5E0D8] overflow-hidden" data-testid="team-members-list">
            <table className="w-full text-sm">
              <thead className="bg-[#FAF7F0]">
                <tr className="text-left text-[#4A4A4A]">
                  <th className="px-4 py-3 font-medium">Name</th>
                  <th className="px-4 py-3 font-medium">Email</th>
                  <th className="px-4 py-3 font-medium">Role</th>
                  <th className="px-4 py-3 font-medium">Joined</th>
                  {canManage && <th className="px-4 py-3 font-medium"></th>}
                </tr>
              </thead>
              <tbody>
                {data.members.map((m) => (
                  <tr key={m.id} className="border-t border-[#E5E0D8]" data-testid={`team-member-row-${m.id}`}>
                    <td className="px-4 py-3 text-[#183D36] font-medium">{m.name || "—"}{m.id === currentUserId && <span className="text-xs text-[#4A4A4A] ml-2">(you)</span>}</td>
                    <td className="px-4 py-3 text-[#4A4A4A]">{m.email}</td>
                    <td className="px-4 py-3">
                      <span className="inline-block px-2 py-0.5 rounded-full bg-[#EFECE5] text-xs uppercase font-accent tracking-wider text-[#183D36]">{m.role || "member"}</span>
                    </td>
                    <td className="px-4 py-3 text-[#4A4A4A] text-xs">{m.created_at ? new Date(m.created_at).toLocaleDateString() : "—"}</td>
                    {canManage && (
                      <td className="px-4 py-3 text-right">
                        {m.id !== currentUserId && m.role !== "admin" && (
                          <button
                            onClick={() => remove(m.id, m.email)}
                            className="text-xs text-red-700 hover:underline"
                            data-testid={`team-member-remove-${m.id}`}
                          >
                            Remove
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {data.pending_invites?.length > 0 && (
          <div className="mt-6" data-testid="team-invites-pending">
            <p className="text-sm font-medium text-[#183D36] mb-2">Pending invitations</p>
            <div className="rounded-2xl border border-[#E5E0D8] overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-[#FAF7F0]">
                  <tr className="text-left text-[#4A4A4A]">
                    <th className="px-4 py-3 font-medium">Email</th>
                    <th className="px-4 py-3 font-medium">Role</th>
                    <th className="px-4 py-3 font-medium">Sent</th>
                    <th className="px-4 py-3 font-medium">Expires</th>
                    {canManage && <th className="px-4 py-3 font-medium"></th>}
                  </tr>
                </thead>
                <tbody>
                  {data.pending_invites.map((i) => (
                    <tr key={i.id} className="border-t border-[#E5E0D8]" data-testid={`team-invite-row-${i.id}`}>
                      <td className="px-4 py-3 text-[#183D36]">{i.email}</td>
                      <td className="px-4 py-3">
                        <span className="inline-block px-2 py-0.5 rounded-full bg-[#F4B183]/40 text-xs uppercase font-accent tracking-wider text-[#183D36]">{i.role}</span>
                      </td>
                      <td className="px-4 py-3 text-[#4A4A4A] text-xs">{i.created_at ? new Date(i.created_at).toLocaleDateString() : "—"}</td>
                      <td className="px-4 py-3 text-[#4A4A4A] text-xs">{i.expires_at ? new Date(i.expires_at).toLocaleDateString() : "—"}</td>
                      {canManage && (
                        <td className="px-4 py-3 text-right">
                          <button
                            onClick={() => revoke(i.id, i.email)}
                            className="text-xs text-red-700 hover:underline"
                            data-testid={`team-invite-revoke-${i.id}`}
                          >
                            Revoke
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function LogoCard({ authed }) {
  const [logo, setLogo] = useState(null);
  const [cacheBuster, setCacheBuster] = useState("");
  const [localPreview, setLocalPreview] = useState("");
  const [imgBroken, setImgBroken] = useState(false);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  // Same-origin URL so preview works even when PUBLIC_SITE_URL still points at production.
  const previewSrc = localPreview
    || (logo?.workspace_id
      ? `${API_BASE}/files/logo/${logo.workspace_id}?v=${encodeURIComponent(cacheBuster || logo.created_at || "")}`
      : "");

  const load = async () => {
    setLoading(true);
    try {
      const r = await authed("/product/logo");
      if (r.ok) {
        const data = await r.json();
        setLogo(data.logo);
        setCacheBuster(data.cache_buster || data.logo?.created_at || "");
        setImgBroken(false);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => () => {
    if (localPreview) URL.revokeObjectURL(localPreview);
  }, [localPreview]);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error("Logo too large (max 2MB)"); return; }
    if (localPreview) URL.revokeObjectURL(localPreview);
    const objectUrl = URL.createObjectURL(file);
    setLocalPreview(objectUrl);
    setImgBroken(false);
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await authed("/product/logo", { method: "POST", body: fd });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || "Upload failed");
      toast.success("Logo saved");
      setLogo(data.logo);
      setCacheBuster(data.cache_buster || String(Date.now()));
      setImgBroken(false);
      URL.revokeObjectURL(objectUrl);
      setLocalPreview("");
    } catch (err) {
      toast.error(String(err.message || err));
      URL.revokeObjectURL(objectUrl);
      setLocalPreview("");
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };

  const remove = async () => {
    if (!window.confirm("Remove your logo?")) return;
    setBusy(true);
    try {
      const r = await authed("/product/logo", { method: "DELETE" });
      if (!r.ok) throw new Error();
      toast.success("Logo removed");
      setLogo(null);
      setCacheBuster("");
      setImgBroken(false);
      if (localPreview) {
        URL.revokeObjectURL(localPreview);
        setLocalPreview("");
      }
    } catch {
      toast.error("Failed to remove");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-3xl bg-white border border-[#E5E0D8] p-8" data-testid="studio-logo-card">
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-2 mb-4">
        <div>
          <h2 className="font-heading text-xl text-[#183D36] mb-1">Your logo</h2>
          <p className="text-sm text-[#4A4A4A]">Appears in the header of every outbound email — thank-yous, review requests, rebook nudges, and win-backs. PNG, JPG, or WEBP. Max 2MB. Aim for a square-ish crop.</p>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row items-start gap-6">
        <div className="w-32 h-32 rounded-2xl bg-[#EFECE5] border border-[#E5E0D8] flex items-center justify-center overflow-hidden" data-testid="studio-logo-preview">
          {loading ? (
            <span className="text-xs text-[#4A4A4A]">Loading…</span>
          ) : previewSrc && !imgBroken ? (
            <img
              src={previewSrc}
              alt="Logo"
              className="w-full h-full object-contain"
              onError={() => setImgBroken(true)}
              onLoad={() => setImgBroken(false)}
            />
          ) : logo ? (
            <span className="text-xs text-[#4A4A4A] text-center px-2">Logo saved — preview unavailable</span>
          ) : (
            <span className="text-xs text-[#4A4A4A] text-center px-2">No logo yet</span>
          )}
        </div>

        <div className="flex-1 space-y-3">
          <label className="inline-flex items-center gap-2 rounded-full bg-[#183D36] hover:bg-[#122e29] text-[#FAF7F0] px-5 h-10 cursor-pointer text-sm font-medium">
            <Upload size={14} />
            <span>{busy ? "Uploading…" : logo ? "Replace logo" : "Upload logo"}</span>
            <input
              type="file"
              accept="image/png,image/jpeg,image/webp"
              onChange={onFile}
              disabled={busy}
              className="hidden"
              data-testid="studio-logo-upload-input"
            />
          </label>
          {logo && (
            <Button
              data-testid="studio-logo-remove-btn"
              variant="ghost"
              onClick={remove}
              disabled={busy}
              className="rounded-full border border-[#183D36]/20 ml-2"
            >
              Remove
            </Button>
          )}
          {logo && (
            <p className="text-xs text-[#4A4A4A]">
              Original: <b>{logo.original_filename}</b> · {logo.size < 1024 ? `${logo.size} B` : `${(logo.size / 1024).toFixed(logo.size < 10240 ? 1 : 0)} KB`} · uploaded {new Date(logo.created_at).toLocaleDateString()}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

function SettingsTab({ settings, templates, authed, onRefresh }) {
  const [s, setS] = useState(settings || { business_name: "", sender_name: "", reply_to_email: "", book_url: "", business_type: "other" });
  const [saving, setSaving] = useState(false);
  const [types, setTypes] = useState([]);
  const [applyingPreset, setApplyingPreset] = useState(false);
  const [presetOpen, setPresetOpen] = useState(false);
  const [pendingType, setPendingType] = useState(null);

  useEffect(() => { if (settings) setS(settings); }, [settings]);
  useEffect(() => {
    (async () => {
      const r = await authed("/product/business-types");
      if (r.ok) setTypes(await r.json());
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const saveSettings = async () => {
    setSaving(true);
    try {
      const r = await authed("/product/settings", { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify(s) });
      if (!r.ok) throw new Error();
      toast.success("Settings saved");
      onRefresh();
    } catch { toast.error("Failed to save"); }
    finally { setSaving(false); }
  };

  const applyPreset = async (bt) => {
    setApplyingPreset(true);
    try {
      const r = await authed("/product/apply-preset", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ business_type: bt }),
      });
      if (!r.ok) throw new Error();
      const data = await r.json();
      toast.success(`${data.label} preset applied`);
      setS({ ...s, business_type: bt });
      setPresetOpen(false);
      setPendingType(null);
      onRefresh();
    } catch { toast.error("Failed to apply preset"); }
    finally { setApplyingPreset(false); }
  };

  const currentType = types.find(t => t.id === s.business_type);

  return (
    <div className="grid gap-6">
      <div className="rounded-3xl bg-white border border-[#E5E0D8] p-8">
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-6">
          <div>
            <h2 className="font-heading text-xl text-[#183D36] mb-1">Business type</h2>
            <p className="text-sm text-[#4A4A4A]">Applying a preset overwrites all four templates with wording, tone, and cadence tuned for that industry. You can still edit them after.</p>
          </div>
        </div>
        <div className="grid gap-4 md:grid-cols-2 items-end">
          <div className="grid gap-2">
            <Label>Your business type</Label>
            <Select value={s.business_type || "other"} onValueChange={(v) => setS({ ...s, business_type: v })}>
              <SelectTrigger data-testid="studio-settings-business-type" className="rounded-full">
                <SelectValue placeholder="Choose one" />
              </SelectTrigger>
              <SelectContent>
                {types.map(t => (
                  <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {currentType && <p className="text-xs text-[#4A4A4A] mt-1">{currentType.description}</p>}
          </div>
          <div>
            <Button
              data-testid="studio-apply-preset-btn"
              onClick={() => { setPendingType(s.business_type); setPresetOpen(true); }}
              disabled={!s.business_type}
              variant="ghost"
              className="rounded-full border border-[#183D36]/20 text-[#183D36] hover:bg-[#EFECE5] w-full md:w-auto"
            >
              Apply preset to templates
            </Button>
          </div>
        </div>
      </div>

      <LogoCard authed={authed} />

      <div className="rounded-3xl bg-white border border-[#E5E0D8] p-8">
        <h2 className="font-heading text-xl text-[#183D36] mb-1">Your voice</h2>
        <p className="text-sm text-[#4A4A4A] mb-6">These values are used in every message that goes to your customers.</p>
        <div className="grid gap-4 md:grid-cols-2">
          <div className="grid gap-2"><Label>Business name</Label><Input data-testid="studio-settings-business" value={s.business_name || ""} onChange={(e) => setS({ ...s, business_name: e.target.value })} /></div>
          <div className="grid gap-2"><Label>Sender name</Label><Input data-testid="studio-settings-sender" value={s.sender_name || ""} onChange={(e) => setS({ ...s, sender_name: e.target.value })} placeholder="Brooke" /></div>
          <div className="grid gap-2"><Label>Reply-to email (optional)</Label><Input data-testid="studio-settings-replyto" type="email" value={s.reply_to_email || ""} onChange={(e) => setS({ ...s, reply_to_email: e.target.value })} placeholder="brooke@bookyournextvisit.com" /></div>
          <div className="grid gap-2"><Label>Booking link</Label><Input data-testid="studio-settings-book" value={s.book_url || ""} onChange={(e) => setS({ ...s, book_url: e.target.value })} placeholder="https://calendly.com/…" /></div>
          <div className="grid gap-2 md:col-span-2">
            <Label>Review link (Google, Yelp, etc. — leave blank to skip review emails)</Label>
            <Input data-testid="studio-settings-review" value={s.review_url || ""} onChange={(e) => setS({ ...s, review_url: e.target.value })} placeholder="https://g.page/r/…/review" />
          </div>
        </div>
        <Button data-testid="studio-settings-save" disabled={saving} onClick={saveSettings} className="mt-6 rounded-full bg-[#183D36] hover:bg-[#122e29] text-[#FAF7F0] px-6">
          {saving ? "Saving…" : "Save voice"}
        </Button>
      </div>

      {["thank_you", "review", "rebook", "winback"]
        .map((k) => templates.find(t => t.kind === k))
        .filter(Boolean)
        .map((tpl) => <TemplateCard key={tpl.kind} tpl={tpl} authed={authed} onSaved={onRefresh} />)}

      <AiDraftCard
        businessType={s.business_type || "other"}
        types={types}
        authed={authed}
        onApplied={onRefresh}
      />

      <Dialog open={presetOpen} onOpenChange={setPresetOpen}>
        <DialogContent className="rounded-3xl bg-[#FAF7F0] max-w-md" data-testid="studio-preset-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl text-[#183D36]">Apply preset?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-[#4A4A4A]">
            This will overwrite all four of your templates — <b>thank-you</b>, <b>review</b>, <b>rebook</b>, and <b>win-back</b> — with the {types.find(t => t.id === pendingType)?.label || "selected"} defaults (tone, wording, cadence). Any edits you made will be replaced.
          </p>
          <DialogFooter className="gap-2">
            <Button variant="ghost" onClick={() => setPresetOpen(false)} className="rounded-full" data-testid="studio-preset-cancel-btn">Keep my templates</Button>
            <Button
              data-testid="studio-preset-confirm-btn"
              disabled={applyingPreset}
              onClick={() => applyPreset(pendingType)}
              className="rounded-full bg-[#183D36] hover:bg-[#122e29] text-[#FAF7F0] px-6"
            >
              {applyingPreset ? "Applying…" : "Apply preset"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const TEMPLATE_PLACEHOLDERS = {
  thank_you: ["first_name", "service", "business_name", "sender_name"],
  review: ["first_name", "service", "review_url", "sender_name"],
  rebook: ["first_name", "service", "business_name", "sender_name", "book_url"],
  winback: ["first_name", "business_name", "sender_name", "book_url"],
};

function placeholderToken(key) {
  return `{${key}}`;
}

function initPlaceholderEdits(kind) {
  const keys = TEMPLATE_PLACEHOLDERS[kind] || [];
  const edits = {};
  for (const key of keys) {
    edits[key] = placeholderToken(key);
  }
  return edits;
}

function TemplateCard({ tpl, authed, onSaved }) {
  const [t, setT] = useState(tpl);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [placeholderEdits, setPlaceholderEdits] = useState(() => initPlaceholderEdits(tpl.kind));
  const [committed, setCommitted] = useState(() => initPlaceholderEdits(tpl.kind));

  useEffect(() => {
    setT(tpl);
    const next = initPlaceholderEdits(tpl.kind);
    setPlaceholderEdits(next);
    setCommitted(next);
  }, [tpl]);

  const applyPlaceholderEdits = () => {
    let subject = t.subject || "";
    let body = t.body || "";
    const keys = TEMPLATE_PLACEHOLDERS[t.kind] || [];
    let changed = false;
    const nextCommitted = { ...committed };
    for (const key of keys) {
      const from = committed[key] || placeholderToken(key);
      const to = placeholderEdits[key] ?? "";
      if (!from || from === to) continue;
      if (!subject.includes(from) && !body.includes(from)) continue;
      subject = subject.split(from).join(to);
      body = body.split(from).join(to);
      nextCommitted[key] = to;
      changed = true;
    }
    if (!changed) {
      toast.message("No placeholder changes to apply");
      return;
    }
    setT({ ...t, subject, body });
    setCommitted(nextCommitted);
    setPlaceholderEdits(nextCommitted);
    toast.success("Placeholders updated in subject and body — save to keep");
  };

  const save = async () => {
    setSaving(true);
    try {
      const r = await authed(`/product/templates/${tpl.kind}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          kind: t.kind,
          enabled: !!t.enabled,
          delay_value: parseInt(t.delay_value, 10),
          delay_unit: t.delay_unit,
          subject: t.subject,
          body: t.body,
        }),
      });
      if (!r.ok) throw new Error();
      toast.success(`${labelForKind(t.kind)} saved`);
      onSaved();
    } catch { toast.error("Failed to save"); }
    finally { setSaving(false); }
  };

  const resetTemplate = async () => {
    if (!window.confirm(`Reset ${labelForKind(t.kind)} to the default for your business type? This restores the original placeholders.`)) return;
    setResetting(true);
    try {
      const r = await authed(`/product/templates/${tpl.kind}/reset`, { method: "POST" });
      if (!r.ok) throw new Error();
      const data = await r.json();
      setT(data);
      const next = initPlaceholderEdits(data.kind);
      setPlaceholderEdits(next);
      setCommitted(next);
      toast.success(`${labelForKind(t.kind)} reset to defaults`);
      onSaved();
    } catch { toast.error("Failed to reset template"); }
    finally { setResetting(false); }
  };

  const keys = TEMPLATE_PLACEHOLDERS[t.kind] || [];

  return (
    <div className="rounded-3xl bg-white border border-[#E5E0D8] p-8" data-testid={`studio-template-${t.kind}`}>
      <div className="flex items-start justify-between mb-4">
        <div>
          <h3 className="font-heading text-xl text-[#183D36]">{labelForKind(t.kind)}</h3>
          <p className="text-sm text-[#4A4A4A] mt-1">
            {t.kind === "winback" ? (
              <>Sends automatically when a customer&apos;s last visit was more than the days below ago and they have no future appointment.</>
            ) : (
              <>Edit placeholders below to update every occurrence in the subject and body at once.</>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-[#4A4A4A]">{t.enabled ? "On" : "Off"}</span>
          <Switch data-testid={`studio-template-toggle-${t.kind}`} checked={!!t.enabled} onCheckedChange={(v) => setT({ ...t, enabled: v })} />
        </div>
      </div>

      {keys.length > 0 && (
        <div className="mb-6 rounded-2xl bg-[#FAF7F0] border border-[#E5E0D8] p-4" data-testid={`studio-template-placeholders-${t.kind}`}>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-3">
            <div>
              <p className="text-sm font-medium text-[#183D36]">Placeholders</p>
              <p className="text-xs text-[#4A4A4A]">Change a value here, then apply — it replaces that text everywhere in this template.</p>
            </div>
            <Button
              type="button"
              data-testid={`studio-template-apply-placeholders-${t.kind}`}
              variant="ghost"
              onClick={applyPlaceholderEdits}
              className="rounded-full border border-[#183D36]/20 text-[#183D36] hover:bg-[#EFECE5] shrink-0"
            >
              Apply to template
            </Button>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            {keys.map((key) => (
              <div key={key} className="grid gap-1.5">
                <Label className="text-xs font-mono text-[#4A4A4A]">{placeholderToken(key)}</Label>
                <Input
                  data-testid={`studio-template-placeholder-${t.kind}-${key}`}
                  value={placeholderEdits[key] ?? ""}
                  onChange={(e) => setPlaceholderEdits({ ...placeholderEdits, [key]: e.target.value })}
                  placeholder={placeholderToken(key)}
                  className="rounded-full font-mono text-sm"
                />
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid gap-4">
        <div className="grid grid-cols-2 gap-4">
          <div className="grid gap-2">
            <Label>{t.kind === "winback" ? "Trigger after" : "Send after"}</Label>
            <Input data-testid={`studio-template-delay-${t.kind}`} type="number" min="0" value={t.delay_value} onChange={(e) => setT({ ...t, delay_value: e.target.value })} />
          </div>
          <div className="grid gap-2">
            <Label>Unit</Label>
            <Select value={t.delay_unit} onValueChange={(v) => setT({ ...t, delay_unit: v })}>
              <SelectTrigger data-testid={`studio-template-unit-${t.kind}`} className="rounded-full"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="minutes">Minutes</SelectItem>
                <SelectItem value="hours">Hours</SelectItem>
                <SelectItem value="days">Days</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="grid gap-2"><Label>Subject</Label><Input data-testid={`studio-template-subject-${t.kind}`} value={t.subject} onChange={(e) => setT({ ...t, subject: e.target.value })} /></div>
        <div className="grid gap-2"><Label>Body</Label><Textarea data-testid={`studio-template-body-${t.kind}`} value={t.body} onChange={(e) => setT({ ...t, body: e.target.value })} className="min-h-[180px] font-mono text-sm" /></div>
      </div>
      <div className="mt-6 flex flex-wrap gap-2">
        <Button data-testid={`studio-template-save-${t.kind}`} disabled={saving || resetting} onClick={save} className="rounded-full bg-[#183D36] hover:bg-[#122e29] text-[#FAF7F0] px-6">
          {saving ? "Saving…" : "Save template"}
        </Button>
        <Button
          data-testid={`studio-template-reset-${t.kind}`}
          disabled={saving || resetting}
          variant="ghost"
          onClick={resetTemplate}
          className="rounded-full border border-[#183D36]/20 text-[#183D36] hover:bg-[#EFECE5] px-6"
        >
          {resetting ? "Resetting…" : "Reset placeholders"}
        </Button>
      </div>
    </div>
  );
}

function labelForKind(kind) {
  return { thank_you: "Thank-you", rebook: "Rebook nudge", review: "Review request", winback: "Win-back" }[kind] || kind;
}

/* -------------------- AI Draft -------------------- */
function AiDraftCard({ businessType, types, authed, onApplied }) {
  const [voice, setVoice] = useState("");
  const [drafts, setDrafts] = useState(null);
  const [drafting, setDrafting] = useState(false);
  const [applying, setApplying] = useState(false);

  const draft = async () => {
    if (!voice.trim()) { toast.error("Describe your voice in a sentence"); return; }
    setDrafting(true);
    setDrafts(null);
    try {
      const r = await authed("/product/ai-draft", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ business_type: businessType, voice_description: voice }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || "AI draft failed");
      setDrafts(data);
      toast.success("Drafts ready — review and apply below");
    } catch (e) {
      toast.error(String(e.message || e));
    } finally {
      setDrafting(false);
    }
  };

  const apply = async () => {
    if (!drafts) return;
    setApplying(true);
    try {
      for (const kind of ["thank_you", "review", "rebook", "winback"]) {
        const d = drafts[kind];
        if (!d) continue;
        const r = await authed(`/product/templates/${kind}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            kind,
            enabled: true,
            delay_value: parseInt(d.delay_value, 10),
            delay_unit: d.delay_unit,
            subject: d.subject,
            body: d.body,
          }),
        });
        if (!r.ok) throw new Error(`Failed to save ${kind}`);
      }
      toast.success("Drafts applied — all four templates updated");
      setDrafts(null);
      setVoice("");
      onApplied();
    } catch (e) {
      toast.error(String(e.message || e));
    } finally {
      setApplying(false);
    }
  };

  const currentTypeLabel = types.find(t => t.id === businessType)?.label || "your business";

  return (
    <div className="rounded-3xl bg-white border border-[#E5E0D8] p-8" data-testid="studio-ai-draft-card">
      <div className="flex items-start justify-between gap-6 mb-4">
        <div className="flex items-center gap-3">
          <span className="w-11 h-11 rounded-2xl bg-[#F4B183] text-[#183D36] flex items-center justify-center">
            <Wand2 size={18} strokeWidth={1.75} />
          </span>
          <div>
            <h2 className="font-heading text-xl text-[#183D36]">Draft with AI</h2>
            <p className="text-sm text-[#4A4A4A]">Describe your voice in a sentence — Claude drafts <b>all four templates</b> (thank-you, review, rebook, win-back) in your style, tuned for <b>{currentTypeLabel}</b>.</p>
          </div>
        </div>
      </div>
      <div className="grid gap-3">
        <Label>Your voice</Label>
        <Textarea
          data-testid="studio-ai-voice-input"
          value={voice}
          onChange={(e) => setVoice(e.target.value)}
          placeholder='e.g. "Warm and personal — I use first names and light humor. Never salesy."'
          className="min-h-[80px]"
        />
        <div className="flex gap-2">
          <Button
            data-testid="studio-ai-draft-btn"
            onClick={draft}
            disabled={drafting || !voice.trim()}
            className="rounded-full bg-[#183D36] hover:bg-[#122e29] text-[#FAF7F0] px-6"
          >
            <Wand2 size={14} className="mr-2" />
            {drafting ? "Drafting…" : "Draft with AI"}
          </Button>
          {drafts && (
            <Button
              data-testid="studio-ai-apply-btn"
              onClick={apply}
              disabled={applying}
              variant="ghost"
              className="rounded-full border border-[#183D36]/20 text-[#183D36] hover:bg-[#EFECE5] px-6"
            >
              {applying ? "Applying…" : "Apply drafts to templates"}
            </Button>
          )}
        </div>
      </div>

      {drafts && (
        <div className="grid gap-4 mt-6" data-testid="studio-ai-drafts-preview">
          {["thank_you", "review", "rebook", "winback"].map((kind) => {
            const d = drafts[kind];
            if (!d) return null;
            return (
              <div key={kind} className="rounded-2xl bg-[#EFECE5] border border-[#E5E0D8] p-5" data-testid={`studio-ai-preview-${kind}`}>
                <div className="flex items-center justify-between mb-3">
                  <span className="text-xs font-accent uppercase tracking-widest text-[#F4B183] font-semibold">{labelForKind(kind)}</span>
                  <span className="text-xs text-[#4A4A4A]">Sends {d.delay_value} {d.delay_unit} after appt</span>
                </div>
                <div className="font-heading text-sm text-[#183D36] mb-2">{d.subject}</div>
                <pre className="text-xs text-[#252525] whitespace-pre-wrap font-sans leading-relaxed" data-testid={`studio-ai-body-${kind}`}>{d.body}</pre>
              </div>
            );
          })}
          <p className="text-xs text-[#4A4A4A]">Click <b>Apply drafts to templates</b> to overwrite all four of your active templates with these drafts, or click <b>Draft with AI</b> again to regenerate.</p>
        </div>
      )}
    </div>
  );
}

/* -------------------- Integrations (Square) -------------------- */
function IntegrationsTab({ authed, onRefresh, onGoToAppointments }) {
  const [status, setStatus] = useState(null);
  const [checking, setChecking] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [connectForm, setConnectForm] = useState({ environment: "sandbox", access_token: "", webhook_signature_key: "" });
  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);

  const load = async () => {
    setChecking(true);
    try {
      const r = await authed("/square/status");
      if (r.ok) setStatus(await r.json());
    } catch { /* ignore */ }
    finally { setChecking(false); }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const runSync = async () => {
    setSyncing(true);
    try {
      const r = await authed("/square/sync?since_hours=168", { method: "POST" });
      const data = await r.json();
      if (!r.ok || data.ok === false) {
        toast.error(data.error || "Sync failed");
      } else {
        toast.success(`Sync complete — ${data.new_appointments} new appt(s), ${data.scheduled_messages} message(s) queued`);
        onRefresh();
        load();
      }
    } catch { toast.error("Sync failed"); }
    finally { setSyncing(false); }
  };

  const runSeed = async () => {
    setSeeding(true);
    try {
      const r = await authed("/square/seed-test-booking", { method: "POST" });
      const data = await r.json();
      if (!r.ok || data.ok === false) {
        toast.error(data.error || "Seed failed");
        if (data.hint) {
          setTimeout(() => toast.info(data.hint, { duration: 12000 }), 400);
        }
      } else {
        toast.success("Test booking created in Square sandbox — click Sync now");
        load();
      }
    } catch { toast.error("Seed failed"); }
    finally { setSeeding(false); }
  };

  const runConnect = async () => {
    if (!connectForm.access_token.trim()) { toast.error("Paste your Square access token first"); return; }
    setConnecting(true);
    try {
      const r = await authed("/square/connect", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(connectForm),
      });
      const data = await r.json();
      if (!r.ok) { toast.error(data.detail || "Connect failed"); return; }
      toast.success("Square connected");
      setConnectForm({ environment: connectForm.environment, access_token: "", webhook_signature_key: "" });
      setStatus(data.status || null);
      load();
    } catch (e) {
      toast.error(String(e.message || e));
    } finally {
      setConnecting(false);
    }
  };

  const runDisconnect = async () => {
    if (!window.confirm("Disconnect this Square account? Your existing customers and appointments stay — new syncs will stop.")) return;
    setDisconnecting(true);
    try {
      const r = await authed("/square/connect", { method: "DELETE" });
      if (!r.ok) { toast.error("Disconnect failed"); return; }
      toast.success("Square disconnected");
      setStatus(null);
      load();
    } catch { toast.error("Disconnect failed"); }
    finally { setDisconnecting(false); }
  };

  const connectedTone = status?.connected && status?.appointments_enabled ? "sage" : status?.connected ? "apricot" : "ivory";
  const statusText =
    !status ? "Checking…" :
    !status.configured ? "Not connected — paste a Square access token below to link your account" :
    !status.connected ? "Connected credentials but no location found — check the token" :
    !status.appointments_enabled ? "Connected — but the sandbox merchant needs Appointments enabled" :
    "Connected & Appointments live";

  return (
    <div className="grid gap-6">
      <div className="rounded-3xl bg-white border border-[#E5E0D8] p-8" data-testid="studio-square-card">
        <div className="flex items-start justify-between gap-6 flex-wrap">
          <div className="flex items-start gap-4">
            <span className={`w-12 h-12 rounded-2xl flex items-center justify-center ${
              connectedTone === "sage" ? "bg-[#A8BFA3]" : connectedTone === "apricot" ? "bg-[#F4B183]" : "bg-[#EFECE5]"
            }`}>
              <Link2 size={20} className="text-[#183D36]" />
            </span>
            <div>
              <div className="overline mb-1">Booking integration</div>
              <h2 className="font-heading text-2xl text-[#183D36]">Square Appointments</h2>
              <div className="flex items-center gap-2 mt-2">
                {status?.connected ? <CheckCircle2 size={14} className="text-[#183D36]" /> : <AlertCircle size={14} className="text-[#F4B183]" />}
                <span className="text-sm text-[#4A4A4A]" data-testid="studio-square-status-text">{checking ? "Checking…" : statusText}</span>
              </div>
              {status?.location_name && (
                <p className="text-xs text-[#4A4A4A] mt-2">
                  Location: <span className="font-medium text-[#183D36]">{status.location_name}</span> <span className="opacity-60">({status.environment})</span>
                </p>
              )}
              {status?.last_sync_at && (
                <p className="text-xs text-[#4A4A4A] mt-1" data-testid="studio-square-last-sync">
                  Last sync: {new Date(status.last_sync_at).toLocaleString()}
                </p>
              )}
              {status?.last_sync_error && (
                <p className="text-xs text-red-600 mt-1 max-w-md truncate" title={status.last_sync_error}>
                  Last error: {(() => {
                    const err = String(status.last_sync_error);
                    const detail = err.match(/'detail':\s*'([^']+)'/);
                    if (detail) return detail[1];
                    return err.length > 140 ? err.slice(0, 140) + "…" : err;
                  })()}
                </p>
              )}
            </div>
          </div>
          <div className="flex gap-2">
            <Button data-testid="studio-square-refresh" variant="ghost" onClick={load} className="rounded-full border border-[#183D36]/20">
              <RefreshCw size={14} className="mr-2" /> Refresh
            </Button>
            {status?.configured && (
              <Button
                data-testid="studio-square-sync"
                onClick={runSync}
                disabled={syncing}
                className="rounded-full bg-[#183D36] hover:bg-[#122e29] text-[#FAF7F0]"
              >
                {syncing ? "Syncing…" : "Sync now"}
              </Button>
            )}
            {status?.configured && (
              <Button
                data-testid="studio-square-disconnect"
                onClick={runDisconnect}
                disabled={disconnecting}
                variant="ghost"
                className="rounded-full border border-red-300 text-red-700 hover:bg-red-50"
              >
                {disconnecting ? "…" : "Disconnect"}
              </Button>
            )}
          </div>
        </div>

        {!status?.configured && !checking && (
          <div className="mt-6 rounded-2xl bg-[#FAF7F0] border border-[#E5E0D8] p-5" data-testid="studio-square-connect-panel">
            <p className="text-sm font-medium text-[#183D36] mb-2">Connect your Square account</p>
            <p className="text-xs text-[#4A4A4A] mb-4">
              Get a Sandbox or Production access token from <a className="text-[#183D36] underline" target="_blank" rel="noreferrer" href="https://developer.squareup.com/apps">Square Developer Console</a> → your app → <b>Credentials</b>. Paste the token below.
            </p>
            <div className="grid gap-3">
              <div className="grid gap-1">
                <Label className="text-xs">Environment</Label>
                <Select value={connectForm.environment} onValueChange={(v) => setConnectForm({ ...connectForm, environment: v })}>
                  <SelectTrigger data-testid="studio-square-env-select" className="rounded-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sandbox">Sandbox (testing)</SelectItem>
                    <SelectItem value="production">Production (real bookings)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-1">
                <Label className="text-xs">Access token</Label>
                <Input
                  data-testid="studio-square-token-input"
                  value={connectForm.access_token}
                  onChange={(e) => setConnectForm({ ...connectForm, access_token: e.target.value })}
                  placeholder="EAAA... or sq0atp-..."
                  type="password"
                />
              </div>
              <div className="grid gap-1">
                <Label className="text-xs">Webhook signing key (optional, for real-time booking events)</Label>
                <Input
                  data-testid="studio-square-webhook-key-input"
                  value={connectForm.webhook_signature_key}
                  onChange={(e) => setConnectForm({ ...connectForm, webhook_signature_key: e.target.value })}
                  placeholder="Only needed if you subscribe to Square webhooks"
                />
              </div>
              <Button
                data-testid="studio-square-connect-btn"
                onClick={runConnect}
                disabled={connecting}
                className="rounded-full bg-[#183D36] hover:bg-[#122e29] text-[#FAF7F0] w-fit"
              >
                {connecting ? "Connecting…" : "Connect Square"}
              </Button>
            </div>
          </div>
        )}

        {!status?.appointments_enabled && status?.connected && (
          <div className="mt-6 rounded-2xl bg-[#FAF7F0] border border-[#E5E0D8] p-5 text-sm text-[#252525]" data-testid="studio-square-not-enabled-panel">
            <p className="font-medium mb-3">Square Appointments isn&apos;t turned on in this sandbox merchant yet. Two paths to fix it:</p>

            <p className="font-medium mt-4 mb-2 text-[#183D36]">Option A — Fastest: seed a test booking via API</p>
            <p className="text-[#4A4A4A] mb-3">One click. If your merchant is capable of Appointments, this creates a customer + a real Square booking for the sync to pick up. If not, you&apos;ll see the exact reason.</p>
            <Button
              data-testid="studio-square-seed-test"
              onClick={runSeed}
              disabled={seeding}
              variant="ghost"
              className="rounded-full border border-[#183D36]/30 text-[#183D36] hover:bg-[#183D36] hover:text-[#FAF7F0]"
            >
              {seeding ? "Creating…" : "Create test booking"}
            </Button>

            <p className="font-medium mt-6 mb-2 text-[#183D36]">Option B — Via the sandbox seller dashboard</p>
            <ol className="list-decimal pl-5 space-y-1 text-[#4A4A4A]">
              <li>Go to your <a href="https://developer.squareup.com/apps" target="_blank" rel="noreferrer" className="text-[#183D36] underline">Square Developer Console</a> → <b>Sandbox test accounts</b>.</li>
              <li>Click <b>Square Dashboard</b> next to &quot;Default Test Account&quot;.</li>
              <li>In the sandbox seller dashboard, look for <b>Appointments</b> in the sidebar or under <b>Items & services → More</b>.</li>
              <li>Complete onboarding — add a service and staff member.</li>
              <li>Come back and click <b>Sync now</b>.</li>
            </ol>
          </div>
        )}
      </div>

      <div className="rounded-3xl bg-white border border-[#E5E0D8] p-8">
        <h3 className="font-heading text-lg text-[#183D36] mb-2">How it works</h3>
        <ul className="text-sm text-[#4A4A4A] space-y-2">
          <li>· Every hour, NextVisit pulls new/updated bookings from Square and mirrors them as customers + appointments here.</li>
          <li>· If you subscribed the <code>booking.created</code> and <code>booking.updated</code> webhooks in your Square Developer Console, each booking triggers scheduling in real time (within seconds).</li>
          <li>· Each new appointment auto-schedules a thank-you, a review request, and a rebook nudge based on your active templates. Stale customers (no visit in your win-back window) get a win-back message automatically.</li>
          <li>· Customers pulled from Square must have an email address for messages to be sent.</li>
        </ul>
      </div>

      <div className="rounded-3xl bg-[#A8BFA3]/30 border border-[#A8BFA3] p-8" data-testid="studio-square-skip-callout">
        <div className="overline mb-2">Square being flaky? Skip it.</div>
        <h3 className="font-heading text-lg text-[#183D36] mb-2">You can test the whole pipeline right now without Square.</h3>
        <p className="text-sm text-[#4A4A4A] mb-4">
          The Appointments tab lets you log an appointment manually — same messaging engine, same templates, no dependency on Square&apos;s sandbox UI (which is notoriously buggy). Log a test appointment for your own email with a datetime a few minutes from now to see the thank-you land in your inbox.
        </p>
        <Button
          data-testid="studio-goto-appointments-btn"
          onClick={() => onGoToAppointments && onGoToAppointments()}
          className="rounded-full bg-[#183D36] hover:bg-[#122e29] text-[#FAF7F0]"
        >
          Log a test appointment
        </Button>
      </div>
    </div>
  );
}
